import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Save } from 'lucide-react';
import { useAdminStore } from '@/lib/admin-store';
import AdminBilingualInput from '@/components/admin/AdminBilingualInput';
import AdminImageUpload from '@/components/admin/AdminImageUpload';
import AdminMultiImageUpload from '@/components/admin/AdminMultiImageUpload';
import toast from 'react-hot-toast';
import type { BilingualText, Artist } from '@/types';

const INPUT_CLS = "w-full px-4 py-3 bg-black/[0.03] dark:bg-white/[0.05] border border-black/[0.06] dark:border-white/[0.08] rounded-xl text-sm font-medium outline-none focus:border-black/20 dark:focus:border-white/20 transition-all placeholder:text-black/20 dark:placeholder:text-white/20";
const LABEL_CLS = "block text-[9px] font-bold uppercase tracking-[0.2em] text-black/40 dark:text-white/40 mb-2";

const AdminExhibitionEditorPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const exhibitions = useAdminStore((s) => s.exhibitions);
  const artists = useAdminStore((s) => s.artists);
  const addExhibition = useAdminStore((s) => s.addExhibition);
  const updateExhibition = useAdminStore((s) => s.updateExhibition);
  const fieldLimits = useAdminStore((s) => s.settings?.fieldLimits);

  const isEditing = id !== undefined && id !== 'new';
  const existing = isEditing ? exhibitions.find((e) => e._id === id) : undefined;

  const [title, setTitle] = useState<BilingualText>({ en: '', ka: '' });
  const [excerpt, setExcerpt] = useState<BilingualText>({ en: '', ka: '' });
  const [description, setDescription] = useState<BilingualText>({ en: '', ka: '' });
  const [aboutArtist, setAboutArtist] = useState<BilingualText>({ en: '', ka: '' });
  const [poster, setPoster] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [location, setLocation] = useState<BilingualText>({ en: '', ka: '' });
  const [isCurrent, setIsCurrent] = useState(false);
  const [images, setImages] = useState<string[]>([]);
  const [selectedArtistIds, setSelectedArtistIds] = useState<string[]>([]);
  const [customSectionEnabled, setCustomSectionEnabled] = useState(false);
  const [customSectionTitle, setCustomSectionTitle] = useState<BilingualText>({ en: '', ka: '' });
  const [customSectionText, setCustomSectionText] = useState<BilingualText>({ en: '', ka: '' });

  useEffect(() => {
    if (existing) {
      setTitle(existing.title);
      setExcerpt(existing.excerpt || { en: '', ka: '' });
      setDescription(existing.description);
      setAboutArtist(existing.aboutArtist || { en: '', ka: '' });
      setPoster(existing.poster);
      setStartDate(existing.startDate);
      setEndDate(existing.endDate);
      setLocation(existing.location);
      setImages(existing.images);
      setIsCurrent(existing.isCurrent);
      setSelectedArtistIds(existing.artists.map((a) => a._id));
      setCustomSectionEnabled(existing.customSection?.enabled ?? false);
      setCustomSectionTitle(existing.customSection?.title ?? { en: '', ka: '' });
      setCustomSectionText(existing.customSection?.text ?? { en: '', ka: '' });
    }
  }, [existing]);

  const toggleArtist = (artistId: string): void => {
    setSelectedArtistIds((prev) =>
      prev.includes(artistId) ? prev.filter((aid) => aid !== artistId) : [...prev, artistId]
    );
  };

  const handleSave = (): void => {
    try {
      if (!title.en.trim()) {
        toast.error('Title (EN) is required');
        return;
      }
      if (!startDate || !endDate) {
        toast.error('Start and end dates are required');
        return;
      }
      const selectedArtists: Artist[] = artists.filter((a) => selectedArtistIds.includes(a._id));
      const customSection = { enabled: customSectionEnabled, title: customSectionTitle, text: customSectionText };
      if (isEditing && existing) {
        updateExhibition(existing._id, { title, excerpt, description, aboutArtist, poster, images, startDate, endDate, location, isCurrent, artists: selectedArtists, customSection });
        toast.success('Exhibition updated successfully!');
      } else {
        addExhibition({ title, excerpt, description, aboutArtist, poster, images, startDate, endDate, location, isCurrent, artists: selectedArtists, customSection });
        toast.success('Exhibition created successfully!');
      }
      navigate('/admin/exhibitions');
    } catch (err) {
      console.error('Failed to save exhibition:', err);
      toast.error('Failed to save exhibition. Please try again.');
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center gap-4">
        <Link to="/admin/exhibitions" className="w-9 h-9 rounded-xl border border-black/[0.06] dark:border-white/[0.06] flex items-center justify-center text-black/30 dark:text-white/30 hover:text-black dark:hover:text-white hover:border-black/15 dark:hover:border-white/15 transition-all">
          <ArrowLeft size={16} />
        </Link>
        <div>
          <h1 className="text-2xl font-syncopate font-bold tracking-tighter">
            {isEditing ? 'Edit Exhibition' : 'New Exhibition'}
          </h1>
          <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.3em] text-black/20 dark:text-white/20">
            {isEditing ? existing?.title.en : 'Create a new exhibition'}
          </p>
        </div>
      </div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }} className="bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl p-6 space-y-5">
        <AdminBilingualInput label="Title" value={title} onChange={setTitle} placeholder="Exhibition title" maxLength={fieldLimits?.title} />
        <AdminBilingualInput label="Short Description" value={excerpt} onChange={setExcerpt} placeholder="Brief description shown on listing page (1-2 sentences)" maxLength={fieldLimits?.shortDescription} />
        <AdminBilingualInput label="Full Description" value={description} onChange={setDescription} textarea placeholder="Full exhibition description shown on detail page" maxLength={fieldLimits?.description} />
        <AdminBilingualInput label="About the Artist" value={aboutArtist} onChange={setAboutArtist} textarea placeholder="Text about the artist shown below the exhibition description" maxLength={fieldLimits?.description} />
        <AdminImageUpload label="Poster" value={poster} onChange={setPoster} shape="rect" />
        <AdminMultiImageUpload label="Gallery Images" value={images} onChange={setImages} />
        <div className="grid grid-cols-2 gap-4">
          <div><label className={LABEL_CLS}>Start Date</label><input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={INPUT_CLS} /></div>
          <div><label className={LABEL_CLS}>End Date</label><input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={INPUT_CLS} /></div>
        </div>
        <AdminBilingualInput label="Location" value={location} onChange={setLocation} placeholder="Gallery name, city" maxLength={fieldLimits?.shortDescription} />
        <div className="flex items-center justify-between">
          <div>
            <label className={LABEL_CLS}>Currently Active</label>
            <p className="text-[10px] text-black/30 dark:text-white/30">Show as current exhibition on the site</p>
          </div>
          <button onClick={() => setIsCurrent(!isCurrent)} className={`w-10 h-5 rounded-full transition-all ${isCurrent ? 'bg-black dark:bg-white' : 'bg-black/10 dark:bg-white/10'}`}>
            <span className={`block w-4 h-4 rounded-full bg-white dark:bg-black transition-transform ${isCurrent ? 'translate-x-5' : 'translate-x-0.5'}`} />
          </button>
        </div>
        <div>
          <label className={LABEL_CLS}>Artists</label>
          <div className="grid grid-cols-2 gap-2 mt-1">
            {artists.map((artist) => (
              <label key={artist._id} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${selectedArtistIds.includes(artist._id) ? 'border-black/20 dark:border-white/20 bg-black/[0.03] dark:bg-white/[0.05]' : 'border-black/[0.06] dark:border-white/[0.06] hover:border-black/10 dark:hover:border-white/10'}`}>
                <input type="checkbox" checked={selectedArtistIds.includes(artist._id)} onChange={() => toggleArtist(artist._id)} className="w-4 h-4 rounded accent-black dark:accent-white" />
                <div className="flex items-center gap-2 min-w-0">
                  <img src={artist.portrait} alt="" className="w-6 h-6 rounded-full object-cover shrink-0" />
                  <span className="text-[11px] font-medium truncate">{artist.name.en}</span>
                </div>
              </label>
            ))}
          </div>
          {artists.length === 0 && <p className="text-[10px] text-black/30 dark:text-white/30 mt-2">No artists available. Add artists first.</p>}
        </div>

        {/* Custom Section */}
        <div className="border-t border-black/[0.04] dark:border-white/[0.04] pt-5 mt-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <label className={LABEL_CLS}>Custom Section</label>
              <p className="text-[10px] text-black/30 dark:text-white/30">Extra section shown after gallery on detail page</p>
            </div>
            <button onClick={() => setCustomSectionEnabled(!customSectionEnabled)} className={`w-10 h-5 rounded-full transition-all ${customSectionEnabled ? 'bg-black dark:bg-white' : 'bg-black/10 dark:bg-white/10'}`}>
              <span className={`block w-4 h-4 rounded-full bg-white dark:bg-black transition-transform ${customSectionEnabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </button>
          </div>
          {customSectionEnabled && (
            <div className="space-y-4">
              <AdminBilingualInput label="Section Title" value={customSectionTitle} onChange={setCustomSectionTitle} placeholder="e.g. Curator's Note, Press Release..." />
              <AdminBilingualInput label="Section Content" value={customSectionText} onChange={setCustomSectionText} textarea placeholder="Section content text..." />
            </div>
          )}
        </div>
      </motion.div>
      <div className="flex items-center gap-3">
        <button onClick={handleSave} className="flex items-center gap-2 px-6 py-3 bg-black dark:bg-white text-white dark:text-black text-[10px] font-bold uppercase tracking-[0.15em] rounded-xl hover:opacity-90 transition-opacity">
          <Save size={14} /> {isEditing ? 'Save Changes' : 'Create Exhibition'}
        </button>
        <Link to="/admin/exhibitions" className="px-6 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-black/40 dark:text-white/40 hover:text-black dark:hover:text-white transition-colors">Cancel</Link>
      </div>
    </div>
  );
};

export default AdminExhibitionEditorPage;
